import React, { useEffect, useState } from 'react';

import Moment from 'moment';

import { FaFileExcel, FaSync, FaFilePdf } from 'react-icons/fa';

import { toast } from 'react-toastify';

import api from '~/services/api';

import { exportCsv } from '~/helpers/csv';
import { exportPdf } from '~/helpers/pdf';

import {
  Scroll,
  Container,
  Header,
  Area,
  Grid,
  GridLine,
  GridHeadItem,
  GridItem,
  Progress,
  Loading,
  Empty,
  Expansive,
  Stack,
  Footer,
  Label,
  Cancel,
  Button,
  Input,
  Box,
  Checkbox,
  Summary,
} from '~/components';

import PedidoType from '~/@types/pedido';
import { useHistory } from 'react-router-dom';
import { LoadArea } from './styles';

let lang = require('moment/locale/pt-br');
Moment.locale('pt-br', lang);

const Pedidos: React.FC = () => {
  const history = useHistory();

  const [inSyncPedidos, setInSyncPedidos] = useState(false);
  const [inSyncFaturamento, setInSyncFaturamento] = useState(false);

  const [inLoad, setInLoad] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoType[]>([]);

  // INTERVALO DE DATAS (FILTRO)
  const [integradoERP, setIntegradoERP] = useState('NAO');
  const [faturado, setFaturado] = useState('NAO');
  const [periodicidade, setPeriodicidade] = useState('hoje');
  const [dtinicio, setDtinicio] = useState('');
  const [dtfinal, setDtfinal] = useState('');

  // DETALHES DO PEDIDO
  const visualizarPedido = (pPedido: PedidoType) => {
    history.push(`pedido/${pPedido.idpedido}`);
  };

  // RESETAR FILTROS
  const resetar = () => {
    setIntegradoERP('NAO');
    setFaturado('NAO');
    setPeriodicidade('tudo');
    setDtfinal('');
    setDtinicio('');
  };

  const aplicarFiltros = async () => {
    try {
      setInLoad(true);
      setPedidos([]);

      const response = await api.get(
        `pedidos?integradoerp=${integradoERP}&faturado=${faturado}&periodicidade=${
          periodicidade ? periodicidade : ''
        }&dtfinal=${dtfinal ? dtfinal : ''}&dtinicio=${
          dtinicio ? dtinicio : ''
        }`,
      );

      const { data } = response;

      if (data) {
        setPedidos(data);
      }

      setInLoad(false);
    } catch (e) {
      setInLoad(false);
    }
  };

  // Pedidos
  const fetchPedidos = async (pStrSearch: string) => {
    try {
      resetar();
      setPedidos([]);
      setInLoad(true);

      const response = await api.get(`pedidos?busca=${pStrSearch}`);

      const { data } = response;

      if (data && data.length) {
        setPedidos(data);
      }

      setInLoad(false);
    } catch (e) {
      setInLoad(false);
    }
  };

  const refetchPedidos = () => {
    aplicarFiltros();
  };

  useEffect(() => {
    aplicarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncPedidos = async () => {
    try {
      setInSyncPedidos(true);

      const response = await api.get(`sync-pedidos`);

      const { data } = response;

      if (data && data.error) {
        toast.error(data.message);
      } else {
        toast.success(data.message);
        aplicarFiltros();
      }

      setInSyncPedidos(false);
    } catch (e) {
      setInSyncPedidos(false);
    }
  };

  const syncFaturamento = async () => {
    try {
      setInSyncFaturamento(true);

      const response = await api.get(`sync-faturamento`);

      const { data } = response;

      if (data && data.error) {
        toast.error(data.message);
      } else {
        toast.success(data.message);
        aplicarFiltros();
      }

      setInSyncFaturamento(false);
    } catch (e) {
      setInSyncFaturamento(false);
    }
  };

  const gerarRelatorioCSV = () => {
    let data: string[] = [];

    data.push(
      'IDPEDIDO;TID;INTEGRADO;FATURADO;DATA;CLIENTE;STATUS;SITUAÇÃO;VALOR',
    );

    let c;
    for (c in pedidos) {
      const pedido = pedidos[c];
      data.push(
        `${pedido.idpedido};${pedido.tiderp};${
          pedido.tiderp !== '' ? 'SIM' : 'NÃO'
        };${pedido.numnfe !== '' ? 'SIM' : 'NÃO'};${pedido.dt};${
          pedido.cliente.nome
        };${pedido.status};${pedido.situacao};${pedido.precototal}`,
      );
    }

    const csv = data.join('\n');

    exportCsv(csv, 'PEDIDOS');
  };

  const gerarRelatorioPDF = () => {
    const cabecalho = [
      'IDPEDIDO',
      'TID',
      'INTEGRADO',
      'FATURADO',
      'DATA',
      'CLIENTE',
      'STATUS',
      'SITUACAO',
      'VALOR',
    ];

    const data: string[][] = [];

    let c;
    for (c in pedidos) {
      const pedido = pedidos[c];
      const linha: string[] = [];

      linha.push(pedido.idpedido);
      linha.push(pedido.tiderp);
      linha.push(`${pedido.tiderp !== '' ? 'SIM' : 'NÃO'}`);
      linha.push(`${pedido.numnfe !== '' ? 'SIM' : 'NÃO'}`);
      linha.push(pedido.dt);
      linha.push(pedido.cliente.nome);
      linha.push(pedido.status);
      linha.push(pedido.situacao);
      linha.push(pedido.precototalformatado);

      data.push(linha);
    }

    exportPdf(cabecalho, data, 'PEDIDOS');
  };

  return (
    <>
      {inLoad ? <Progress /> : null}

      <Scroll>
        <Container>
          <Header
            title="Pedidos"
            subtitle="Gerencie a sincronização dos pedidos"
            isSearch
            onSearch={fetchPedidos}
            placeholder="Buscar em pedidos..."
            isRefresh
            onRefresh={refetchPedidos}
          />

          <Expansive
            title="FILTROS"
            closeLabel="FECHAR FILTROS"
            openLabel="ABRIR FILTROS"
          >
            <Stack>
              <Label
                number="1"
                label="Integrado ao ERP?"
                subtitle="Pediodos já sincronizados ou não"
                status={!!integradoERP}
              />
              <Box>
                <Checkbox
                  label="SIM"
                  onClick={() => setIntegradoERP('SIM')}
                  status={integradoERP === 'SIM'}
                />
                <Checkbox
                  label="NÃO"
                  onClick={() => setIntegradoERP('NAO')}
                  status={integradoERP === 'NAO'}
                />
              </Box>
            </Stack>

            <Stack>
              <Label
                number="2"
                label="Faturado?"
                subtitle="Pediodos já faturados ou não"
                status={!!faturado}
              />
              <Box>
                <Checkbox
                  label="SIM"
                  onClick={() => setFaturado('SIM')}
                  status={faturado === 'SIM'}
                />
                <Checkbox
                  label="NÃO"
                  onClick={() => setFaturado('NAO')}
                  status={faturado === 'NAO'}
                />
              </Box>
            </Stack>

            <Stack>
              <Label
                number="3"
                label="Intervalo"
                subtitle="Selecione o intervalo desejado"
                status={!!periodicidade}
              />
              <Box>
                <Checkbox
                  label="Tudo"
                  onClick={() => setPeriodicidade('tudo')}
                  status={periodicidade === 'tudo'}
                />
                <Checkbox
                  label="Hoje"
                  onClick={() => setPeriodicidade('hoje')}
                  status={periodicidade === 'hoje'}
                />
                <Checkbox
                  label="Semana"
                  onClick={() => setPeriodicidade('semana')}
                  status={periodicidade === 'semana'}
                />
                <Checkbox
                  label="Personalizado"
                  onClick={() => setPeriodicidade('intervalo')}
                  status={periodicidade === 'intervalo'}
                />
              </Box>
            </Stack>

            {periodicidade === 'intervalo' ? (
              <Stack>
                <Label
                  number="3.1"
                  label="De:"
                  subtitle="Informe uma data de início"
                  status={!!dtinicio}
                />
                <Input
                  type="text"
                  placeholder="Data de início..."
                  value={dtinicio}
                  onChange={e => setDtinicio(e.target.value)}
                  mask={'99/99/9999'}
                />
              </Stack>
            ) : null}

            {periodicidade === 'intervalo' ? (
              <Stack>
                <Label
                  number="3.2"
                  label="Até:"
                  subtitle="Informe uma data limite"
                  status={!!dtfinal}
                />
                <Input
                  type="text"
                  placeholder="Data limite..."
                  value={dtfinal}
                  onChange={e => setDtfinal(e.target.value)}
                  mask={'99/99/9999'}
                />
              </Stack>
            ) : null}

            <Footer>
              <Button onClick={aplicarFiltros}>APLICAR</Button>
              <Cancel onClick={resetar}>Resetar</Cancel>
            </Footer>
          </Expansive>

          <Summary>
            {pedidos.length ? (
              <h1>
                {pedidos.length > 1
                  ? `${pedidos.length} pedidos em ${
                      integradoERP === 'NAO' ? 'fila de sincronização' : 'tela'
                    }`
                  : `Um pedido em ${
                      integradoERP === 'NAO' ? 'fila de sincronização' : 'tela'
                    }`}
              </h1>
            ) : (
              <h1>
                {integradoERP === 'NAO'
                  ? `Nenhum pedido em fila no momento`
                  : `Nenhum pedido em tela no momento`}
              </h1>
            )}
            <div>
              <button
                className="sync"
                onClick={syncPedidos}
                title="Sincronizar Pedidos"
                disabled={inSyncPedidos}
              >
                {inSyncPedidos ? (
                  <Loading color="#fff" size={18} />
                ) : (
                  <FaSync size={16} color="#fff" />
                )}
                <div>
                  <h3>SINCRONIZAR</h3>
                  <h2>PEDIDOS</h2>
                </div>
              </button>

              <button
                className="sync"
                onClick={syncFaturamento}
                title="Sincronizar Faturamento"
                disabled={inSyncFaturamento}
              >
                {inSyncFaturamento ? (
                  <Loading color="#fff" size={18} />
                ) : (
                  <FaSync size={16} color="#fff" />
                )}
                <div>
                  <h3>SINCRONIZAR</h3>
                  <h2>FATURAMENTO</h2>
                </div>
              </button>

              {pedidos.length ? (
                <>
                  <button
                    className="excel"
                    onClick={gerarRelatorioCSV}
                    title="Exportar para Excel"
                  >
                    <FaFileExcel size={22} color="#fff" />
                    <div>
                      <h3>EXPORTAR</h3>
                      <h2>EXCEL</h2>
                    </div>
                  </button>
                  <button
                    className="pdf"
                    onClick={gerarRelatorioPDF}
                    title="Exportar para PDF"
                  >
                    <FaFilePdf size={22} color="#fff" />
                    <div>
                      <h3>EXPORTAR</h3>
                      <h2>PDF</h2>
                    </div>
                  </button>
                </>
              ) : null}
            </div>
          </Summary>

          {!inLoad ? (
            <Area>
              {pedidos.length ? (
                <Grid>
                  <GridLine>
                    <GridHeadItem title="ID" width={100} />
                    <GridHeadItem title="TID" width={100} />
                    <GridHeadItem title="Integrado?" width={80} />
                    <GridHeadItem title="Faturado?" width={80} />
                    <GridHeadItem title="Data" width={160} />
                    <GridHeadItem title="Cliente" />
                    <GridHeadItem title="Status" width={180} />
                    <GridHeadItem title="Situação" width={180} />

                    <GridHeadItem title="Valor" width={120} />
                  </GridLine>

                  {pedidos.map((pedido, key) => (
                    <GridLine
                      key={pedido.idpedido}
                      onClick={() => visualizarPedido(pedido)}
                    >
                      <GridItem width={100}>{pedido.idpedido}</GridItem>
                      <GridItem width={100}>
                        {pedido.tiderp !== '' ? pedido.tiderp : '...'}
                      </GridItem>
                      <GridItem width={80}>
                        <span
                          style={{
                            color: pedido.tiderp !== '' ? '#14681c' : '#c03',
                          }}
                        >
                          {pedido.tiderp ? 'SIM' : 'NÃO'}
                        </span>
                      </GridItem>
                      <GridItem width={80}>
                        <span
                          style={{
                            color: pedido.numnfe !== '' ? '#14681c' : '#c03',
                          }}
                        >
                          {pedido.numnfe ? 'SIM' : 'NÃO'}
                        </span>
                      </GridItem>

                      <GridItem width={160}>
                        <span>{pedido.dt}</span>
                        <br />
                        <small>
                          {Moment(pedido.dt, 'DD/MM/YYYY HH:mm').fromNow()}
                        </small>
                      </GridItem>
                      <GridItem>
                        {pedido.cliente.tipo === 'PF' ? (
                          <strong>
                            {pedido.cliente.nome} {pedido.cliente.sobrenome} (
                            {pedido.cliente.cpf})
                          </strong>
                        ) : (
                          <strong>
                            {pedido.cliente.razaosocial} ({pedido.cliente.cnpj})
                          </strong>
                        )}
                      </GridItem>
                      <GridItem width={180}>{pedido.status}</GridItem>
                      <GridItem width={180}>{pedido.situacao}</GridItem>

                      <GridItem width={120}>
                        {pedido.precototalformatado}
                      </GridItem>
                    </GridLine>
                  ))}
                </Grid>
              ) : (
                <Empty>
                  {integradoERP === 'NAO'
                    ? 'Nenhum pedido em fila no momento'
                    : 'Nenhum pedido em tela, selecione os filtros e aplique para visualizar pedidos'}
                </Empty>
              )}
            </Area>
          ) : (
            <LoadArea>
              <Loading color="#000" size={36} />
            </LoadArea>
          )}
        </Container>
      </Scroll>
    </>
  );
};

export default Pedidos;
